
import React, { useEffect, useState } from "react";
import { MessageCircle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import Intercom from "@intercom/messenger-js-sdk";

// This is a client-side implementation, but for production
// the HMAC should be generated on your server
const generateUserHash = async (email: string): Promise<string> => {
  try {
    // For demonstration only - in production, the HMAC should be generated on your server
    // The secret key should never be exposed in client-side code
    const secretKey = 'IZwLZFSlAk1rva_PJpcQzQLoenx3vzcJLpswTbtzDh4';
    
    // Convert secret key to ArrayBuffer
    const encoder = new TextEncoder();
    const keyData = encoder.encode(secretKey);
    
    // Import the key
    const cryptoKey = await window.crypto.subtle.importKey(
      'raw',
      keyData,
      { name: 'HMAC', hash: 'SHA-256' },
      false,
      ['sign']
    );
    
    // Sign the email
    const emailData = encoder.encode(email);
    const signature = await window.crypto.subtle.sign(
      'HMAC',
      cryptoKey,
      emailData
    );
    
    // Convert to hex string
    return Array.from(new Uint8Array(signature))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');
  } catch (error) {
    console.error('Error generating user hash:', error);
    return '';
  }
};

const IntercomChat = () => {
  const { user } = useAuth();
  const [isChatOpen, setIsChatOpen] = useState(false);

  useEffect(() => {
    const initIntercom = async () => {
      if (user) {
        // Generate HMAC for user verification
        const userHash = await generateUserHash(user.email);
        
        Intercom({
          app_id: 'ha4jy6e7',
          user_id: user.email, // Using email as unique identifier
          name: user.name,
          email: user.email,
          user_hash: userHash, // Add HMAC verification
          // No timestamp available, so we're not passing created_at
        });
      } else {
        // Initialize Intercom without user data for visitors
        Intercom({
          app_id: 'ha4jy6e7',
        });
      }
    };
    
    initIntercom();

    // Clean up function
    return () => {
      // Close Intercom when component unmounts
      if (window.Intercom) {
        window.Intercom('shutdown');
      }
    };
  }, [user]);

  const toggleChat = () => {
    if (window.Intercom) {
      if (isChatOpen) {
        window.Intercom('hide');
        setIsChatOpen(false);
      } else {
        window.Intercom('show');
        setIsChatOpen(true);
      }
    }
  };

  return (
    <button 
      className="fixed bottom-6 right-6 bg-[#41c7f0] hover:bg-[#3ab7de] text-white rounded-full p-3 shadow-lg z-50 transition-colors"
      onClick={toggleChat}
      aria-label={isChatOpen ? 'Chat schließen' : 'Chat starten'}
    >
      <MessageCircle size={24} />
    </button>
  );
};

export default IntercomChat;
